/**
 * Display helpers for telemetry geo codes.
 *
 * Country names come from the built-in Intl.DisplayNames (no manual table), and
 * the flag is derived algorithmically from the ISO-3166-1 alpha-2 code via
 * regional-indicator symbols. Swedish regions (län) use a small ISO-3166-2 map
 * because geoip returns the bare subdivision code (e.g. "AB").
 */

/** Convert a 2-letter country code to its flag emoji (🇸🇪 etc.). */
export function countryFlag(code: string): string {
  if (!/^[A-Za-z]{2}$/.test(code)) return "🏳️";
  const base = 0x1f1e6;
  const cc = code.toUpperCase();
  return String.fromCodePoint(
    base + (cc.charCodeAt(0) - 65),
    base + (cc.charCodeAt(1) - 65),
  );
}

/** Localised country name, falling back to the raw code. */
export function countryName(code: string, locale: string): string {
  try {
    const dn = new Intl.DisplayNames([locale], { type: "region" });
    return dn.of(code.toUpperCase()) || code;
  } catch {
    return code;
  }
}

/** ISO-3166-2:SE subdivision code → län name. */
const SE_REGIONS: Record<string, string> = {
  AB: "Stockholm",
  AC: "Västerbotten",
  BD: "Norrbotten",
  C: "Uppsala",
  D: "Södermanland",
  E: "Östergötland",
  F: "Jönköping",
  G: "Kronoberg",
  H: "Kalmar",
  I: "Gotland",
  K: "Blekinge",
  M: "Skåne",
  N: "Halland",
  O: "Västra Götaland",
  S: "Värmland",
  T: "Örebro",
  U: "Västmanland",
  W: "Dalarna",
  X: "Gävleborg",
  Y: "Västernorrland",
  Z: "Jämtland",
};

/** Swedish län name for a region code, falling back to the raw code. */
export function seRegionName(code: string): string {
  return SE_REGIONS[code.toUpperCase()] || code;
}
